<template>
	<SideNavLayout>
		<div class="activity-page">
			<div v-if="!isInitialLoadComplete" class="populated-state" aria-busy="true">
				<div class="page-header">
					<h1>{{ $t("nav.activity") }}</h1>
					<span class="skeleton-block skeleton-subtitle" aria-hidden="true"></span>
				</div>

				<div class="activity-tabs">
					<div class="tabs-skeleton" aria-hidden="true">
						<span v-for="n in 3" :key="n" class="skeleton-block skeleton-tab"></span>
					</div>
					<span class="skeleton-block skeleton-mark-as-read" aria-hidden="true"></span>
				</div>

				<hr class="divider" />

				<div class="activity-content">
					<div class="activity-list">
						<div class="cards" aria-hidden="true">
							<div v-for="n in 6" :key="n" class="activity-card-skeleton">
								<span class="skeleton-block skeleton-icon"></span>
								<div class="skeleton-lines">
									<span class="skeleton-block skeleton-line"></span>
									<span class="skeleton-block skeleton-line short"></span>
								</div>
							</div>
						</div>
					</div>
					<div v-if="!isMobile" class="activity-details">
						<div class="details-skeleton" aria-hidden="true">
							<span class="skeleton-block skeleton-detail-header"></span>
							<span class="skeleton-block skeleton-detail-line"></span>
							<span class="skeleton-block skeleton-detail-line short"></span>
							<span class="skeleton-block skeleton-detail-block"></span>
						</div>
					</div>
				</div>
			</div>

			<div
				v-else-if="hasNotifications"
				class="populated-state"
				:class="{ 'mobile-detail': isMobile && mobileDetailOpen }"
			>
				<template v-if="!isMobile || !mobileDetailOpen">
					<div class="page-header">
						<h1>{{ $t("nav.activity") }}</h1>
						<p class="subtitle">
							{{ store.unreadNotificationCount }}
							{{ $t(store.unreadNotificationCount < 2 ? "activityPage.unread" : "activityPage.unreadPlural") }}
						</p>
					</div>

					<div class="activity-tabs">
						<ArkBaseTabs v-model="selectedTab" :items="activityTabs" :defaultValue="ActivityType.All" variant="pill" />

						<Button class="mark-as-read" variant="tertiary" size="m" shape="pill" @click="markAllAsReadByTab">
							<span class="double-check-mark" aria-hidden="true"></span>
							{{ $t("activityPage.markAsRead") }}
							<span class="unread-activities-selected-tab">{{ selectedTabUnreadCount }}</span>
						</Button>
					</div>
				</template>

				<template v-else>
					<button
						type="button"
						class="mobile-back-button"
						:aria-label="$t('activityPage.back')"
						:title="$t('activityPage.back')"
						@click="closeMobileDetail"
					>
						<AppIcon :name="IconName.ChevronLeft" class="back-icon" />
					</button>

					<ActivityDetailHeader
						v-if="selectedActivity"
						inline
						:notification-topic="selectedActivity.notificationTopic"
						:title="selectedActivity.title"
						:timestamp="selectedActivity.timestamp"
					/>
				</template>

				<hr class="divider" />

				<div class="activity-content">
					<div v-if="!isMobile || !mobileDetailOpen" class="activity-list">
						<div v-if="activitySections.length === 0" class="tab-empty-state">
							<span class="notification-icon" aria-hidden="true">
								<span class="icon"></span>
							</span>
							<h2>{{ $t("activityPage.tabEmptyState.title") }}</h2>
							<p>{{ $t("activityPage.tabEmptyState.description") }}</p>
						</div>

						<template v-else>
							<div ref="activitySectionsElement" class="activity-sections">
								<section v-for="section in activitySections" :key="section.type" class="activity-section">
									<h2>{{ $t(`activityPage.sections.${section.type}`) }}</h2>

									<div class="cards">
										<ActivityCard
											v-for="activity in section.activities"
											:key="activity.id"
											:activity="activity"
											:selected="selectedActivity?.id === activity.id"
											@select="onSelectActivity(activity)"
											@delete="deleteActivity(activity)"
										/>
									</div>
								</section>
							</div>

							<footer v-if="selectedTabCount > 0" class="pagination-footer">
								<Pagination
									:page="currentPage"
									:count="selectedTabCount"
									:page-size="ACTIVITY_PAGE_SIZE"
									@update:page="handlePageChange"
								/>
							</footer>
						</template>
					</div>
					<div v-if="!isMobile || mobileDetailOpen" class="activity-details">
						<ActivityDetailCard
							v-if="selectedActivityDetail"
							:activity-detail="selectedActivityDetail"
							:loading="isLoadingDetails"
							:load-error="hasDetailLoadError"
							:show-header="!(isMobile && mobileDetailOpen)"
							@view-search="viewSearch"
							@view-project="viewProject"
						/>
					</div>
				</div>
			</div>

			<ActivityEmptyState v-else @enable-notifications="enableNotifications" @create-search="createSearch" />
		</div>
	</SideNavLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useMediaQuery } from "@vueuse/core"
import ActivityDetailCard from "@/components/activity/ActivityDetailCard.vue"
import ActivityDetailHeader from "@/components/activity/ActivityDetailHeader.vue"
import ActivityCard from "@/components/activity/ActivityCard.vue"
import ActivityEmptyState from "@/components/activity/ActivityEmptyState.vue"
import ArkBaseTabs from "@/components/ui/ArkBaseTabs.vue"
import { config, Feature } from "@/AppConfig"
import Button from "@/components/ui/Button.vue"
import { ACTIVITY_PAGE, ACTIVITY_PAGE_SIZE } from "@/components/constants"
import Pagination from "@/components/ui/Pagination.vue"
import SideNavLayout from "@/components/layout/SideNavLayout.vue"
import { UseRootStore } from "@/model/RootStore"
import { Route } from "@/utils/routing"
import { useCustomViewStore } from "@/stores/customViewStore"
import { ActivityType } from "@/graphql"
import { LinkType, type Activity, type ActivityDetail } from "@/types/activity"
import { notificationToActivity } from "@/utils/activity"
import type { Notification } from "@/utils/ApiClient"
import { startOfDay, startOfWeek } from "@/utils/date"

const { t } = useI18n()
const store = UseRootStore()
const customViewStore = useCustomViewStore()
const router = useRouter()

enum ActivitySectionType {
	TODAY = "today",
	THIS_WEEK = "thisWeek",
	EARLIER = "earlier",
}

type ActivitySection = {
	type: ActivitySectionType
	activities: Activity[]
}

async function markAsRead(activity: Activity): Promise<void> {
	if (activity.readAt) {
		return
	}

	await store.markNotificationRead(activity.id)
}

const isMobile = useMediaQuery("(max-width: 768px)")
const mobileDetailOpen = ref(false)

const selectedActivityDetail = ref<ActivityDetail | null>(null)
const selectedActivity = computed(() => selectedActivityDetail.value?.activity ?? null)
const isLoadingDetails = ref(false)
const hasDetailLoadError = ref(false)

async function onSelectActivity(activity: Activity): Promise<void> {
	await selectActivity(activity)

	if (isMobile.value) {
		mobileDetailOpen.value = true
	}
}

function closeMobileDetail(): void {
	mobileDetailOpen.value = false
}

async function selectActivity(activity: Activity): Promise<void> {
	selectedActivityDetail.value = {
		activity,
		opportunities: [],
		savedSearch: null,
	}
	isLoadingDetails.value = true
	hasDetailLoadError.value = false

	const selectedActivityId = activity.id
	void markAsRead(activity).catch(error => console.error(error))

	try {
		const [opportunities, savedSearch] = await Promise.all([
			store.fetchOpportunitiesByIds(
				activity.opportunityIds,
				activity.linkType === LinkType.PROJECT ? activity.projectId : null
			),
			activity.savedSearchId ? customViewStore.fetchCustomView(activity.savedSearchId) : Promise.resolve(null),
		])

		if (selectedActivity.value?.id !== selectedActivityId) {
			return
		}

		selectedActivityDetail.value = {
			activity,
			opportunities,
			savedSearch,
		}
	} catch (error) {
		console.error(error)

		if (selectedActivity.value?.id === selectedActivityId) {
			hasDetailLoadError.value = true
		}
	} finally {
		if (selectedActivity.value?.id === selectedActivityId) {
			isLoadingDetails.value = false
		}
	}
}

async function viewSearch(savedSearchId: string): Promise<void> {
	const savedSearch = selectedActivityDetail.value?.savedSearch

	if (!savedSearch || savedSearch.id !== savedSearchId) {
		return
	}

	customViewStore.applyCustomView(savedSearch)

	await router.push({
		name: Route.Home,
		query: selectedActivity.value?.projectId ? { project: selectedActivity.value.projectId } : {},
	})
}

async function viewProject(projectId: string): Promise<void> {
	await router.push({ name: Route.Home, query: { project: projectId } })
}

type ActivityTab = ActivityType.All | ActivityType.Projects | ActivityType.Searches

const selectedTab = ref<ActivityTab>(ActivityType.All)
const currentPage = ref(ACTIVITY_PAGE)
const activitySectionsElement = ref<HTMLElement | null>(null)
const isInitialLoadComplete = ref(false)
const hasNotifications = computed(() => store.notificationCount > 0)
const activities = computed<Activity[]>(() =>
	store.notifications.map((notification: Notification) => notificationToActivity(notification, store.userProjects))
)

async function markAllAsReadByTab(): Promise<void> {
	await store.markAllAsRead(selectedTab.value)
	currentPage.value = Math.min(currentPage.value, pageCount.value)
	await fetchActivityPage()
}

async function selectFirstActivity(): Promise<void> {
	const firstActivity = activities.value[0]

	if (!firstActivity) {
		selectedActivityDetail.value = null
		isLoadingDetails.value = false
		hasDetailLoadError.value = false

		return
	}

	if (selectedActivity.value?.id === firstActivity.id) {
		return
	}

	await selectActivity(firstActivity)
}

watch(
	activities,
	activities => {
		if (activities.some(activity => activity.id === selectedActivity.value?.id)) {
			return
		}

		void selectFirstActivity()
	},
	{ flush: "sync" }
)

async function deleteActivity(activity: Activity): Promise<void> {
	const wasSelected = selectedActivity.value?.id === activity.id
	await store.deleteNotification(activity.id)

	if (wasSelected && !store.notifications.some((notification: Notification) => notification.id === activity.id)) {
		await selectFirstActivity()
	}
}

const activitySections = computed<ActivitySection[]>(() => {
	const today = startOfDay(new Date())
	const weekStart = startOfWeek(today)

	return [
		{
			type: ActivitySectionType.TODAY,
			activities: activities.value.filter(activity => activity.timestamp >= today),
		},
		{
			type: ActivitySectionType.THIS_WEEK,
			activities: activities.value.filter(activity => activity.timestamp >= weekStart && activity.timestamp < today),
		},
		{
			type: ActivitySectionType.EARLIER,
			activities: activities.value.filter(activity => activity.timestamp < weekStart),
		},
	].filter(section => section.activities.length > 0)
})

const activityCounts = computed<Record<ActivityTab, number>>(() => ({
	[ActivityType.All]: store.notificationCount,
	[ActivityType.Projects]: store.projectNotificationCount,
	[ActivityType.Searches]: store.searchNotificationCount,
}))

const activityUnreadCounts = computed<Record<ActivityTab, number>>(() => ({
	[ActivityType.All]: store.unreadNotificationCount,
	[ActivityType.Projects]: store.projectUnreadNotificationCount,
	[ActivityType.Searches]: store.searchUnreadNotificationCount,
}))

const activityTabIds: ActivityTab[] = [ActivityType.All, ActivityType.Projects, ActivityType.Searches]

const activityTabs = computed(() =>
	activityTabIds.map(id => ({
		id,
		label: t(`activityPage.tabs.${id}`),
		count: activityCounts.value[id],
	}))
)

const selectedTabCount = computed(
	() => activityTabs.value.find(activityTab => activityTab.id === selectedTab.value)?.count ?? 0
)
const selectedTabUnreadCount = computed(() => activityUnreadCounts.value[selectedTab.value] ?? 0)
const pageCount = computed(() => Math.max(1, Math.ceil(selectedTabCount.value / ACTIVITY_PAGE_SIZE)))

async function fetchActivityPage(): Promise<void> {
	await store.fetchNotifications({
		limit: ACTIVITY_PAGE_SIZE,
		offset: (currentPage.value - 1) * ACTIVITY_PAGE_SIZE,
		activityType: selectedTab.value,
	})
}

async function enableNotifications(): Promise<void> {
	await router.push("/settings")
}

async function createSearch(): Promise<void> {
	await router.push({ name: Route.Home })
}

async function handlePageChange(page: number): Promise<void> {
	currentPage.value = page

	if (activitySectionsElement.value) {
		activitySectionsElement.value.scrollTop = 0
	}

	await fetchActivityPage()
}

watch(selectedTab, async () => {
	currentPage.value = ACTIVITY_PAGE
	closeMobileDetail()

	if (activitySectionsElement.value) {
		activitySectionsElement.value.scrollTop = 0
	}

	await fetchActivityPage()
	await selectFirstActivity()
})

watch(pageCount, async value => {
	if (currentPage.value > value) {
		currentPage.value = value
		await fetchActivityPage()
	}
})

onMounted(async () => {
	try {
		if (!store.CurrentUserDetails) {
			await store.fetchCurrentUserDetails()
		}

		await Promise.all([fetchActivityPage(), store.fetchActivityTabCounts()])
	} finally {
		isInitialLoadComplete.value = true
	}

	await selectFirstActivity()
})
</script>

<style scoped>
.activity-page {
	position: relative;
	height: 100%;
	min-height: 0;
	overflow: hidden;
	font-family: var(--font-family-primary);
}

.populated-state {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);
	padding: var(--spacing-xl);
	height: 100%;
	min-height: 0;
	box-sizing: border-box;
	overflow: hidden;
}

.skeleton-block {
	display: block;
	border-radius: var(--border-radius-sm);
	background: var(--neutral-200);
	animation: skeleton-pulse 1.2s ease-in-out infinite;
}

.skeleton-subtitle {
	width: var(--space-100);
	height: var(--font-size-small);
	margin-top: var(--spacing-xs);
}

.tabs-skeleton {
	display: flex;
	gap: var(--spacing-xxs);
}

.skeleton-tab {
	width: var(--space-80);
	height: var(--size-button);
	border-radius: var(--border-radius-full);
}

.skeleton-mark-as-read {
	width: var(--space-160);
	height: var(--size-button-lg);
	border-radius: var(--border-radius-full);
}

.activity-card-skeleton {
	display: flex;
	align-items: center;
	gap: var(--space-12);
	padding: var(--spacing-card-padding-sm);

	.skeleton-icon {
		width: var(--size-button-lg);
		height: var(--size-button-lg);
		border-radius: var(--border-radius-lg);
		flex: 0 0 auto;
	}

	.skeleton-lines {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.skeleton-line {
		width: 100%;
		height: var(--font-size-regular);

		&.short {
			width: 40%;
		}
	}
}

.details-skeleton {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-md);

	.skeleton-detail-header {
		width: 60%;
		height: var(--font-size-large);
	}

	.skeleton-detail-line {
		width: 100%;
		height: var(--font-size-regular);

		&.short {
			width: 30%;
		}
	}

	.skeleton-detail-block {
		width: 100%;
		height: var(--space-160);
		border-radius: var(--border-radius-lg);
	}
}

@keyframes skeleton-pulse {
	0%,
	100% {
		opacity: 0.58;
	}

	50% {
		opacity: 1;
	}
}

.page-header {
	h1 {
		margin: 0;
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--neutral-900);
	}

	.subtitle {
		margin: var(--spacing-xs) 0 0;
		font-size: var(--font-size-small);
		color: var(--neutral-600);
	}
}

.activity-tabs {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--spacing-md);
	margin-right: calc(var(--spacing-xl) * -1);
	margin-left: calc(var(--spacing-xl) * -1);
	padding-right: var(--spacing-xl);
	padding-left: var(--spacing-md);
	padding-bottom: var(--spacing-sm);

	.mark-as-read {
		flex: none;
		gap: var(--spacing-xs);
		align-items: center;
		/* height: 42px; */

		.double-check-mark {
			width: var(--size-icon-md);
			height: var(--size-icon-md);
			background-color: currentColor;
			mask: url("/icons/double-check-mark.svg") center / contain no-repeat;
		}

		.unread-activities-selected-tab {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: var(--size-icon-lg);
			height: var(--size-icon-lg);
			margin-left: auto;
			background-color: var(--accent-highlight-light);
			color: var(--primary-green);
			border-radius: var(--border-radius-full);
			font-size: var(--font-size-xs);
		}
	}
}

.activity-tabs :deep(.tabs-pill [data-part="list"]) {
	width: fit-content;
	gap: var(--spacing-xxs);
	padding: var(--spacing-xs);
	border-radius: var(--border-radius-full);
	background: var(--neutral-100);
}

.activity-tabs :deep(.tabs-pill [data-part="trigger"][aria-selected="true"]) {
	background-color: var(--neutral-0);
	color: var(--neutral-900);
	box-shadow: var(--shadow-sm);
}

.activity-tabs :deep(.tabs-pill [data-part="trigger"]) {
	padding-top: var(--spacing-xs);
	padding-bottom: var(--spacing-xs);
	line-height: var(--line-height-tight);
}

.activity-tabs :deep(.tabs-pill .tab-count) {
	min-width: var(--size-icon-md);
	height: var(--size-icon-md);
}

.divider {
	margin: calc(var(--spacing-lg) * -1) calc(var(--spacing-xl) * -1) 0;
	border: 0;
	border-top: var(--border-width-thin) solid var(--neutral-200);
}

.activity-content {
	display: grid;
	grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
	flex: 1;
	min-height: 0;
	margin: calc(var(--spacing-lg) * -1) calc(var(--spacing-xl) * -1) calc(var(--spacing-xl) * -1);
	overflow: hidden;

	.activity-list,
	.activity-details {
		min-width: 0;
		min-height: 0;
	}

	.activity-list {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
		padding: var(--spacing-md);
		overflow: hidden;

		.activity-sections {
			display: flex;
			flex: 1;
			flex-direction: column;
			gap: var(--spacing-lg);
			min-height: 0;
			overflow-y: auto;
		}

		.tab-empty-state {
			display: flex;
			flex: 1;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			gap: var(--spacing-xs);
			min-height: 0;
			padding: var(--spacing-xl);
			text-align: center;

			.notification-icon {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				width: var(--space-64);
				height: var(--space-64);
				margin-bottom: var(--spacing-sm);
				border-radius: var(--border-radius-full);
				background-color: var(--accent-highlight-light);
				color: var(--primary-green);

				.icon {
					width: var(--size-icon-xl);
					height: var(--size-icon-xl);
					background-color: currentColor;
					mask: url("/icons/activity-bell.svg") center / contain no-repeat;
				}
			}

			h2 {
				margin: 0;
				color: var(--neutral-900);
				font-size: var(--font-size-base);
				font-weight: var(--font-weight-semibold);
			}

			p {
				margin: 0;
				color: var(--neutral-600);
				font-size: var(--font-size-small);
			}
		}

		.pagination-footer {
			display: flex;
			flex: none;
			justify-content: flex-end;
			margin: 0 calc(var(--spacing-md) * -1) calc(var(--spacing-md) * -1);
			padding: var(--space-18);
			border-top: var(--border-width-thin) solid var(--neutral-200);
		}
	}

	.activity-details {
		border-left: var(--border-width-thin) solid var(--neutral-200);
		overflow-y: auto;
		padding: var(--spacing-xl);
	}
}

.activity-section {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);

	h2 {
		margin: 0 var(--spacing-xs);
		color: var(--neutral-400);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		letter-spacing: 0.02em;
		line-height: var(--line-height-tight);
		text-transform: uppercase;
	}

	.cards {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}
}

.mobile-back-button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--size-button);
	height: var(--size-button);
	padding: 0;
	border: 0;
	border-radius: var(--border-radius-md);
	background: transparent;
	color: var(--neutral-600);
	cursor: pointer;

	.back-icon {
		width: var(--size-icon-md);
		height: var(--size-icon-md);
		fill: none;
		stroke: currentColor;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	&:hover,
	&:focus-visible {
		background: var(--neutral-100);
		outline: none;
	}
}

@media (prefers-reduced-motion: reduce) {
	.skeleton-block {
		animation: none;
	}
}

@media (max-width: 768px) {
	.populated-state {
		display: grid;
		grid-template-columns: 1fr auto;
		grid-template-rows: auto auto auto 1fr;
		grid-template-areas:
			"header button"
			"tabs tabs"
			"divider divider"
			"content content";
		row-gap: var(--spacing-sm);
		padding: var(--spacing-md);
	}

	.populated-state.mobile-detail {
		grid-template-columns: auto 1fr auto;
		grid-template-rows: auto auto auto 1fr;
		grid-template-areas:
			"back  title badge"
			".     time  ."
			"divider divider divider"
			"content content content";
		column-gap: var(--spacing-sm);
		padding-top: var(--spacing-lg);
		padding-bottom: var(--spacing-lg);
		padding-left: var(--spacing-sm);
	}

	.activity-tabs {
		display: contents;
	}

	.page-header {
		grid-area: header;
	}

	.mobile-back-button {
		grid-area: back;
		align-self: start;
	}

	.activity-tabs .mark-as-read,
	.activity-tabs .skeleton-mark-as-read {
		grid-area: button;
		align-self: center;
	}

	.activity-tabs :deep(.tabs),
	.activity-tabs .tabs-skeleton {
		grid-area: tabs;
	}

	.activity-tabs :deep(.tabs-pill [data-part="list"]),
	.activity-tabs .tabs-skeleton {
		width: 100%;
	}

	.activity-tabs :deep(.tabs-pill [data-part="trigger"]),
	.activity-tabs .tabs-skeleton .skeleton-tab {
		flex: 1;
		justify-content: center;
		padding: var(--spacing-xs) var(--spacing-sm);
		color: var(--neutral-600);
		font-weight: var(--font-weight-medium);
	}

	.activity-tabs :deep(.tabs-pill .tab-count) {
		display: none;
	}

	.divider {
		grid-area: divider;
		margin: 0 calc(var(--spacing-md) * -1);
	}

	.activity-content {
		grid-area: content;
		grid-template-columns: 1fr;
		margin: 0 calc(var(--spacing-md) * -1) calc(var(--spacing-md) * -1);
	}

	.activity-content .activity-details {
		border-left: none;
		padding: var(--spacing-sm) var(--spacing-md) var(--spacing-sm) var(--spacing-lg);
	}

	.activity-content .activity-list .pagination-footer {
		padding: var(--spacing-sm) var(--spacing-md);
	}

	.pagination-footer :deep(.pagination) {
		gap: var(--spacing-xxs);
		font-size: var(--font-size-xs);
	}

	.pagination-footer :deep(.trigger),
	.pagination-footer :deep(.item),
	.pagination-footer :deep(.ellipsis) {
		height: var(--size-button-s);
		min-width: var(--size-button-s);
	}

	.pagination-footer :deep(.trigger),
	.pagination-footer :deep(.item) {
		padding: 0 var(--spacing-xs);
	}

	.pagination-footer :deep(.trigger span) {
		font-size: var(--font-size-small);
	}
}
</style>
